/**
 * api.js — the booth's only route to the server.
 *
 * Trimmed from the source app's 600-line client. Everything here is used by
 * the booth; nothing else was carried across.
 *
 * ⚠️ The one substantive change from the original: the Authorization header
 * carries a BOOTH TOKEN, not a Supabase access token. See lib/boothSession.js
 * and docs/DECISIONS.md D-04.
 */
import { getBoothToken, clearBoothToken } from './boothSession';
import { operatorHeaders, clearOperatorKey } from './operator';
import { report } from './report';

const BASE = '/api';

async function authHeaders() {
  try {
    const token = await getBoothToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    // A booth-session failure must not stop a client_error report from being
    // filed — that is the one call that has to survive everything else.
    return {};
  }
}

/* Which part of the guest's journey a failing endpoint belongs to, so the
   health check can say "uploads are failing" rather than "something 500'd". */
function stageOf(prefix) {
  if (/sign_uploads|upload/i.test(prefix)) return 'upload';
  if (/booth-session/i.test(prefix)) return 'claim';
  if (/status/i.test(prefix)) return 'poll';
  if (/generate|selfie(?!-status)/i.test(prefix)) return 'claim';
  return 'unknown';
}

function failure(prefix, res, text, latency) {
  const err = new Error(`${prefix}: ${res.status} — ${text}`);
  err.status = res.status;
  err.latency = latency;

  /* ⚠️ THE SERVER'S OWN SENTENCE, KEPT SEPARATELY FROM THE DEBUG ONE. `message`
     is built for a log: endpoint, status, raw body. Showing that to a guest at a
     fairground produces `POST send-photo: 400 — {"error":"That does not look
     like an email address."}` on a phone. Functions here answer with a plain
     `{ error }` written FOR the person, so lift it out and let a caller choose
     which audience it is talking to. */
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed.error === 'string') err.serverError = parsed.error;
  } catch { /* not JSON; the raw text is all there is */ }
  if (res.status === 401 || res.status === 403) clearBoothToken();

  /* ⚠️ EVERY SERVER FAILURE THE BOOTH SEES NOW LEAVES A ROW, and this is the
     single funnel every call passes through — which is why the reporting goes
     here rather than at twenty call sites that would each have to remember.

     401 and 403 are excluded on purpose: an expired booth token is the system
     working, and a festival's worth of them would bury the failures that mean
     something. */
  if (res.status !== 401 && res.status !== 403) {
    report(stageOf(prefix), `${res.status} ${String(text).slice(0, 200)}`, { elapsedMs: latency });
  }
  return err;
}

/**
 * ⚠️ THE OPERATOR HEADER IS SENT ONLY WHERE IT IS NEEDED, and `operatorHeaders()`
 * is empty when nobody has unlocked. A guest's request therefore carries no
 * operator credential at all, rather than an empty or malformed one.
 */
async function apiFetch(endpoint, params = {}, { operator = false } = {}) {
  const url = new URL(`${BASE}/${endpoint}`, window.location.origin);
  Object.entries(params).forEach(([k, v]) => {
    if (v != null) url.searchParams.set(k, String(v));
  });

  const start = performance.now();
  const res = await fetch(url.toString(), {
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders()),
      ...(operator ? operatorHeaders() : {}),
    },
    credentials: 'same-origin',
  });
  const latency = Math.round(performance.now() - start);

  if (!res.ok) {
    const text = await res.text().catch(() => 'Unknown error');
    /* A rejected operator key is stale or wrong. Drop it so the next attempt
       asks again instead of silently retrying a credential that will not work. */
    if (operator && (res.status === 401 || res.status === 403)) clearOperatorKey();
    throw failure(`API ${endpoint}${params.q ? '?q=' + params.q : ''}`, res, text, latency);
  }

  const data = await res.json();
  // Attached for the Ledger's latency column.
  if (typeof data === 'object' && data !== null && !Array.isArray(data)) data._latency = latency;
  return data;
}

async function apiPost(endpoint, body, { operator = false } = {}) {
  const res = await fetch(`${BASE}/${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders()),
      /* ⚠️ OPT-IN, exactly like apiFetch. The operator secret opens every
         guest's face; it goes only where it is needed, never on every call by
         default. Forgetting it produces a clean 403 rather than a leak, which
         is the correct direction for this mistake to fail in. */
      ...(operator ? operatorHeaders() : {}),
    },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => 'Unknown error');
    throw failure(`POST ${endpoint}`, res, text);
  }
  return res.json();
}

/**
 * POST to an endpoint that may answer with NO BODY.
 *
 * ⚠️ LOAD-BEARING, and the comment is carried over verbatim because it cost a
 * debugging session. Netlify BACKGROUND functions always reply 202 with an
 * empty body — the platform acknowledges the invocation and the function keeps
 * running detached, so there is nothing to parse. Calling res.json() on that
 * throws "Unexpected end of JSON input". Use this for any `*-background`
 * endpoint; the real result arrives via polling, not this response.
 */
async function apiPostAccepted(endpoint, body) {
  const res = await fetch(`${BASE}/${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => 'Unknown error');
    throw failure(`POST ${endpoint}`, res, text);
  }
  const text = await res.text().catch(() => '');
  if (!text.trim()) return { accepted: true, status: res.status };
  try { return JSON.parse(text); }
  catch { return { accepted: true, status: res.status, raw: text.slice(0, 200) }; }
}

/**
 * Upload one frame to a signed Storage URL.
 *
 * ⚠️ Replaces supabase.storage.uploadToSignedUrl, deliberately. A signed upload
 * URL is a plain HTTP endpoint — the token embedded in it authorises the write,
 * not the client library. Doing it with fetch means @supabase/supabase-js never
 * enters the guest bundle, which:
 *   - keeps the anon key AND the project URL out of the browser entirely, so
 *     the Netlify secrets scanner stays a real guard rather than something we
 *     have to write an exemption for;
 *   - drops a sizeable dependency from a page loaded on festival wifi, where
 *     bundle size is a guest-facing feature.
 *
 * `sign_uploads` already returns a fully-formed `signedUrl` per frame
 * (createSignedUploadUrl gives us one), so the client never has to know where
 * Storage lives. The token fallback exists only for older responses.
 */
async function uploadSignedFrame({ signedUrl, path, token, blob, supabaseUrl, bucket }) {
  const url = signedUrl
    || `${supabaseUrl}/storage/v1/object/upload/sign/${bucket}/${path}?token=${encodeURIComponent(token)}`;
  const res = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'image/jpeg', 'x-upsert': 'true' },
    body: blob,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Storage upload ${res.status} — ${text.slice(0, 200)}`);
  }
  return path;
}

export const api = {
  booth: {
    // POST returns 202 immediately - the work happens in a BACKGROUND function
    // (image gen is 20-60s, well past Netlify's ~26s synchronous gateway).
    // Always follow this with status() polling; there is no synchronous result.
    generate: (payload) => apiPostAccepted('renfest-selfie-background', payload),
    status: (job_id) => apiFetch('renfest-selfie-status', { job_id }),
    runs: (limit) => apiFetch('renfest-selfie-status', { q: 'runs', limit }, { operator: true }),
    // ADMIN ONLY, and fetched one run at a time on demand. These are the raw
    // reference selfies behind a portrait; signing them for every row of the
    // ledger would be dozens of Storage round trips and would put a wall of
    // strangers' faces on screen whether anyone asked for them or not.
    sources: (job_id) => apiFetch('renfest-selfie-status', { q: 'sources', job_id }, { operator: true }),
    score: (payload) => apiPost('renfest-selfie-status', payload),
    /* Email the guest a LINK to their framed portrait (D-50). Not an
       attachment, and not the clean portrait - the framed composite, which the
       generator has already built by the time this is callable. */
    sendPhoto: (job_id, email, marketing_consent) => apiPost('send-photo', { job_id, email, marketing_consent }),
    /* ── the $5 print, bought on the guest's own phone ───────────────
       ⚠️ TWO CALLS BECAUSE THEY COST DIFFERENT AMOUNTS. `printOffer` reads one
       row and is safe to call on render; `printCheckout` reaches Shaman's
       sign-link over the network and mints a live payment link, so it belongs
       behind a deliberate tap and nowhere else.

       ⚠️ AND THE PRICE COMES FROM THE SERVER, NEVER FROM A LITERAL HERE. It is
       per-arm (netlify/functions/_pricing.js) — a hardcoded "$5" in a component
       is wrong for half the guests the moment a second arm is switched on, and
       a button that promises one number while the register charges another is
       the fastest way to lose both the sale and the trust. */
    /* ── the Shoppe basket, sent ahead to the counter ────────────────
       ⚠️ `shoppeHoldEnabled` IS A FEATURE SWITCH READ AT RUNTIME, not a build
       flag. It answers from `event.config.shoppe.hold_orders` so the flow can
       be turned off mid-festival without a deploy, which is the only moment
       anybody will actually want to turn it off. Anything other than a clean
       `enabled: true` means the guest gets the old "show the list" behaviour,
       which still works. Treat a throw as `false`. */
    shoppeHoldEnabled: () => apiFetch('shoppe-order'),
    shoppeOrder: (payload) => apiPost('shoppe-order', payload),

    printOffer: (job_id) => apiFetch('print-checkout', { job_id }),
    printCheckout: (job_id) => apiPost('print-checkout', { job_id }),

    // Records a failure that never reached the generator (e.g. a 413), so it
    // still shows up in the run log instead of vanishing on the phone.
    clientError: (payload) => apiPost('renfest-selfie-status', { ...payload, action: 'client_error' }),
    // Frames go to Storage directly, NOT through the generator: background
    // functions are invoked async and AWS caps async Lambda payloads at 256 KB.
    signUploads: (job_id, count) => apiPost('renfest-selfie-status', { action: 'sign_uploads', job_id, count }),
    uploadFrame: uploadSignedFrame,
  },

  /* The counter at booth #383. Operator-gated: every call carries the
     x-operator-secret header, and the endpoint is 503 until it is configured. */
  counter: {
    /* ⚠️ tenant and event are REQUIRED. `recall` refuses without them — see the
       header of netlify/functions/recall.js. An unscoped lookup is how one
       client's staff end up looking at another client's guests. */
    /* ⚠️ `thumbs` is a real switch, not decoration. Faces on by default so a
       staffer can identify a guest by looking at them; `thumbs: false` sends
       `thumbs=0` and the endpoint returns rows with no image, for a slow tent
       connection or a staffer who does not want faces on screen in a queue. */
    search: (tenant, event, q, thumbs = true) => apiFetch(
      'recall',
      { tenant, event, q, ...(thumbs ? {} : { thumbs: '0' }) },
      { operator: true },
    ),
    open: (tenant, event, code) => apiFetch('recall', { tenant, event, code }, { operator: true }),
    /* Today's numbers for the staff pulse. Polled, so it must stay cheap - see
       the ceiling note in netlify/functions/recall.js. */
    stats: (tenant, event) => apiFetch('recall', { tenant, event, stats: 1 }, { operator: true }),
    /* ⚠️ Take payment at the counter when the POS is not the one taking it -
       cash, a comp, or a POS outage. Idempotent at the database: a second call
       finds `paid` already true and answers 409, so a double-tap cannot charge
       twice. See the mark_paid block in netlify/functions/recall.js. */
    markPaid: (tenant, event, code, by, note) => apiPost(
      `recall?tenant=${encodeURIComponent(tenant)}&event=${encodeURIComponent(event)}`,
      { action: 'mark_paid', code, by, note }, { operator: true },
    ),
    /* Mark the wheel prize handed over. Idempotent at the database: a second
       call finds prize_redeemed_at already set and answers 409. */
    redeem: (tenant, event, code, by) => apiPost(
      `recall?tenant=${encodeURIComponent(tenant)}&event=${encodeURIComponent(event)}`,
      { code, by }, { operator: true },
    ),
  },

  /* The Apothecary Shoppe's price and availability feed.

     ⚠️ A SIBLING OF `booth`, NOT A MEMBER OF IT. verify-repo.mjs asserts that
     api.booth still carries the nine calls the guest path needs, precisely
     because a new sub-object was once inserted before booth's closing brace and
     silently moved three of them into `counter`. Read the block boundaries
     before adding anything here.

     ⚠️ NEVER THE SOURCE OF WHAT A GUEST SEES. RenFestShop.jsx is curated and
     jurisdiction-filtered; this supplies price, availability and a product id to
     MAP onto it. docs/POS-INTEGRATION.md §1. */
  shoppe: {
    products: (pos_id) => apiFetch('pos-products', pos_id ? { pos_id } : {}),

    /* What staff have switched OFF for this event (sql/041). Public, tiny, and
       cached for fifteen seconds rather than five minutes, because the whole
       point of the control is that pulling a product takes effect now. */
    shelf: (tenant, event) => apiFetch('shoppe-shelf', { tenant, event }),
  },
};

// The ported components still say `api.renfestSelfie.*`. Aliased rather than
// renamed at 200 call sites during a port whose whole value is being a clean
// diff (D-08). The alias goes away with the rename in Stage 3.
api.renfestSelfie = api.booth;

export default api;
